import React from "react";
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  useTheme,
} from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloseIcon from "@mui/icons-material/Close";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { RootState } from "utils/store";
import { moveFileSignRequest } from "redux/appSlice";
import { isTruthy } from "helpers/methods";
import { RequestFile } from "models/interfaces";
import { AppModal } from "global/components/AppModal";
import CustomProgressBar from "global/components/CustomProgressBar/CustomProgressBar";
import MyTemplates from "screens/Templates/MyTemplates";
import UpgradeModal from "global/components/UpgradeModal/UpgradeModal";
import MaintenanceBanner from "global/components/MaintenanceBanner/MaintenanceBanner";
import FeatureKeys from "global/constants/FeatureKeys";
import { MobileSignRequestStyles } from "../MobileSignRequest.styles";
import { pressableProps } from "../mobileA11y";
import {
  useDocumentUpload,
  ACCEPTED_FILE_EXTENSIONS,
} from "../hooks/useDocumentUpload";

const FORMAT_HINT =
  "JPEG, JPG, PNG, GIF, BMP, TIFF, PDF, DOC, DOCX, TXT, XLS, XLSX, HEIC format(s) up to 10 MB";

const getFileExtBadge = (name: string) => {
  const ext = name.includes(".")
    ? name.substring(name.lastIndexOf(".") + 1)
    : "DOC";
  return ext.slice(0, 4).toUpperCase();
};

const humanizeSize = (bytes?: number) => {
  if (!bytes || Number.isNaN(bytes)) return "";
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

interface MobileUploadDocumentProps {
  updateTemplate?: boolean;
  isNewTemplate?: boolean;
}

const MobileUploadDocument: React.FC<MobileUploadDocumentProps> = ({
  updateTemplate,
  isNewTemplate,
}) => {
  const theme = useTheme();
  const classes = MobileSignRequestStyles(theme);
  const dispatch = useAppDispatch();
  const errors = useAppSelector((state: RootState) => state.app.errors);
  const isMobile = useAppSelector((state: RootState) => state.app.isMobile);
  const chipLoader = useAppSelector((state: RootState) => state.app.chipLoader);

  const {
    signRequest,
    isRequest,
    docLimit,
    fileInputRef,
    isMultiDocAvailable,
    isTemplateUseAvailable,
    handleUploadClick,
    handleFileInputChange,
    handleDeleteFile,
    isTemplateModalOpen,
    openTemplateModal,
    closeTemplateModal,
    upgradeOpen,
    setUpgradeOpen,
    maintenanceOpen,
    setMaintenanceOpen,
    maintenanceMsg,
  } = useDocumentUpload({ updateTemplate, isNewTemplate });

  const hasFiles = signRequest.files && signRequest.files.length > 0;
  const showFileError = !hasFiles && Boolean(errors.file);

  // Reorder uploaded documents by dragging (long-press on touch), mirroring the
  // desktop chip DnD — same `moveFileSignRequest` dispatch.
  const handleOnDragEnd = (result: any) => {
    if (
      isTruthy(result?.source?.index) &&
      isTruthy(result?.destination?.index)
    ) {
      dispatch(
        moveFileSignRequest({
          fromIndex: result.source.index,
          toIndex: result.destination.index,
        }),
      );
    }
  };

  const fileSubtitle = (file: RequestFile) => {
    if (file.isUploading) return "Uploading…";
    const size = humanizeSize((file as any).size);
    return size || `${getFileExtBadge(file.unsignedDoc || "")} document`;
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple={isMultiDocAvailable}
        accept={ACCEPTED_FILE_EXTENSIONS.join(",")}
        onChange={handleFileInputChange}
        style={{ display: "none" }}
      />

      {/* Select Document card */}
      <Box sx={classes.card}>
        <Typography sx={classes.cardTitle}>Select Document</Typography>

        <Box
          sx={classes.dropzone(showFileError)}
          {...pressableProps(handleUploadClick, {
            label: `Upload document. Accepted formats: ${FORMAT_HINT}`,
          })}
          aria-invalid={showFileError || undefined}
          aria-describedby={showFileError ? "mobile-upload-error" : undefined}
        >
          <Box sx={classes.dropzoneIconWrap}>
            {/* Colour comes from the wrapper, which carries the shared
                dropzone accent. */}
            <FileUploadOutlinedIcon sx={{ fontSize: 20 }} />
          </Box>
          <Typography sx={classes.dropzoneTitle}>Tap to upload</Typography>
          <Typography sx={classes.dropzoneHint}>{FORMAT_HINT}</Typography>
        </Box>

        {showFileError && (
          <Typography
            id="mobile-upload-error"
            role="alert"
            sx={{
              color: theme.palette.error.main,
              fontSize: "0.75rem",
              fontWeight: 500,
              mt: 1,
            }}
          >
            {typeof errors.file === "string"
              ? errors.file
              : "Please upload a document."}
          </Typography>
        )}

        {isRequest && (
          <>
            <Box sx={classes.orRow}>
              <Typography sx={classes.orText}>OR</Typography>
            </Box>

            <Box
              sx={{
                ...classes.templateRow,
                opacity: isTemplateUseAvailable ? 1 : 0.6,
                pointerEvents: isTemplateUseAvailable ? "auto" : "none",
              }}
              {...pressableProps(openTemplateModal, {
                label: "Choose a template from your saved templates",
                disabled: !isTemplateUseAvailable,
                hasPopup: "dialog",
              })}
            >
              <Box sx={classes.templateIconWrap}>
                <DescriptionOutlinedIcon
                  sx={{
                    color: theme.palette.mobile.ePurple[500],
                    fontSize: 20,
                  }}
                />
              </Box>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography sx={classes.templateTitle}>
                  Choose a Template
                </Typography>
                <Typography sx={classes.templateSubtitle}>
                  From your saved templates
                </Typography>
              </Box>
              <ArrowForwardIosIcon
                sx={{ fontSize: 16, color: theme.palette.mobile.grey[400] }}
              />
            </Box>
          </>
        )}
      </Box>

      {/* Uploaded documents */}
      {hasFiles && (
        <>
          <Typography sx={classes.uploadedLabel}>
            Uploaded Documents ({signRequest.files.length}
            {docLimit ? `/${docLimit}` : ""})
          </Typography>

          <DragDropContext onDragEnd={handleOnDragEnd}>
            <Droppable droppableId="mobile-files">
              {(dropProvided: any) => (
                <Box
                  ref={dropProvided.innerRef}
                  {...dropProvided.droppableProps}
                >
                  {signRequest.files.map((file: RequestFile, index: number) => {
                    const draggableId =
                      file.unsignedDoc || file.unsignedPdf || `file-${index}`;
                    return (
                      <Draggable
                        key={draggableId}
                        draggableId={draggableId}
                        index={index}
                      >
                        {(dragProvided: any) => (
                          <Box
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            {...dragProvided.dragHandleProps}
                            sx={{ mb: 1.5 }}
                          >
                            <CustomProgressBar
                              uploadIsCompleted={
                                !file.isUploading && !chipLoader
                              }
                              gradient={classes.fileGradient}
                            >
                              <Box
                                sx={{
                                  ...classes.fileRow,
                                  mb: 0,
                                  position: "relative",
                                  zIndex: 2,
                                  bgcolor: "transparent",
                                }}
                              >
                                <Box aria-hidden="true" sx={classes.fileIconWrap}>
                                  <Typography sx={classes.fileIconText}>
                                    {getFileExtBadge(file.unsignedDoc || "")}
                                  </Typography>
                                </Box>
                                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                  <Typography sx={classes.fileName}>
                                    {file.unsignedDoc}
                                  </Typography>
                                  <Typography sx={classes.fileSize}>
                                    {fileSubtitle(file)}
                                  </Typography>
                                </Box>
                                {file.isUploading ? (
                                  <CircularProgress
                                    size={18}
                                    aria-label="Uploading document"
                                    sx={{
                                      color: theme.palette.mobile.ePurple[500],
                                      mr: 0.5,
                                    }}
                                  />
                                ) : (
                                  <IconButton
                                    sx={classes.fileRemove}
                                    size="small"
                                    aria-label={`Remove ${file.unsignedDoc || "document"}`}
                                    onClick={() => handleDeleteFile(index)}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>
                            </CustomProgressBar>
                          </Box>
                        )}
                      </Draggable>
                    );
                  })}
                  {dropProvided.placeholder}
                </Box>
              )}
            </Droppable>
          </DragDropContext>
        </>
      )}

      {/* Template picker */}
      <AppModal
        open={isTemplateModalOpen}
        onClose={closeTemplateModal}
        title="Choose a template"
        size="lg"
        disableBodyPadding
      >
        <MyTemplates
          isShowCreateBtn={false}
          isShowMoreIcon={false}
          isUseTemplate={true}
          handleCloseTemplateModal={closeTemplateModal}
          isMobile={isMobile}
        />
      </AppModal>

      <UpgradeModal
        open={upgradeOpen}
        featureKey={FeatureKeys.ENVELOPE_MULTI_DOCUMENT}
        onClose={() => setUpgradeOpen(false)}
      />
      <MaintenanceBanner
        open={maintenanceOpen}
        message={maintenanceMsg}
        onClose={() => setMaintenanceOpen(false)}
      />
    </>
  );
};

export default MobileUploadDocument;
